import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { getFirestore, collection, getDocs, query, where, orderBy } from 'firebase/firestore';
import AdminLayout from './AdminLayout';
import DayCloseReport from './DayCloseReport';
import TaxReport from './TaxReport';
import TaxHoldingsTab from './TaxHoldingsTab';
import YearEndTab from './YearEndTab';
import { reportTz, tzDateKey, tzHour, tzDayRangeUtc, tzToday, tzShiftDays } from './tzUtils';
import { useSelectedLocation } from './LocationContext';
import {
  AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from 'recharts';

const COLORS = ['#ff6b35', '#ff8f35', '#ffb347', '#ffcc80', '#ffa726', '#fb8c00', '#f57c00', '#ef6c00'];

// Card payments arrive under several paymentType values depending on the
// channel: 'card' (online), 'terminal' (kiosk/POS reader), 'tap-to-pay',
// 'stripe' (legacy web). Filtering by plain 'card' missed most card sales.
const CARD_PAYMENT_TYPES = ['card', 'terminal', 'tap-to-pay', 'stripe'];
const paymentMatchesFilter = (s, filter) => {
  const pt = (s.paymentType || s.paymentMethod || 'cash').toString().toLowerCase();
  if (filter === 'card') return CARD_PAYMENT_TYPES.includes(pt);
  return pt === filter;
};

const SalesReports = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [locations, setLocations] = useState([]);
  const [activeTab, setActiveTab] = useState('charts'); // charts | dayclose | tax
  const locationCtx = useSelectedLocation();
  const workingLocation = locationCtx?.selectedLocation || null;
  // Reports read the LOCATION's clock, never the consultant's (Vegas/Paris rule).
  const reportTzValue = reportTz(workingLocation);
  const [rawSales, setRawSales] = useState([]);

  // Filters
  const [dateRange, setDateRange] = useState('last30days');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const [selectedLocations, setSelectedLocations] = useState([]);
  const [statusFilter, setStatusFilter] = useState('all');
  const [paymentFilter, setPaymentFilter] = useState('all');
  const [orderTypeFilter, setOrderTypeFilter] = useState('all');

  const navigate = useNavigate();
  const auth = getAuth();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser) {
        setUser(currentUser);
        await loadLocations(currentUser.uid);
        await loadSales(currentUser.uid);
      } else {
        navigate('/admin/login');
      }
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (user) {
      loadSales(user.uid);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateRange, customStart, customEnd, statusFilter, paymentFilter, orderTypeFilter]);

  const loadLocations = async (uid) => {
    try {
      const db = getFirestore();
      const snapshot = await getDocs(collection(db, 'users', uid, 'locations'));
      const locs = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
      setLocations(locs);
      setSelectedLocations(locs.map(l => l.id));
    } catch (e) {
      console.error('Error loading locations:', e);
    }
  };

  const loadSales = async (uid) => {
    setLoading(true);
    try {
      const db = getFirestore();
      const { start, end } = getDateRange();

      const constraints = [
        where('orderDate', '>=', start),
        where('orderDate', '<=', end),
        orderBy('orderDate', 'desc'),
      ];

      if (statusFilter !== 'all') {
        constraints.push(where('status', '==', statusFilter));
      }
      if (paymentFilter !== 'all') {
        // Card FAMILY: card payments are written as 'card' (online), 'terminal'
        // (kiosk/POS reader) and 'tap-to-pay' — filtering only 'card' missed
        // the others entirely (prod report: "card shows zero").
        if (paymentFilter === 'card') {
          constraints.push(where('paymentType', 'in', CARD_PAYMENT_TYPES));
        } else {
          constraints.push(where('paymentType', '==', paymentFilter));
        }
      }
      if (orderTypeFilter !== 'all') {
        constraints.push(where('orderType', '==', orderTypeFilter));
      }

      const salesQuery = query(collection(db, 'users', uid, 'sales'), ...constraints);

      const snapshot = await getDocs(salesQuery);
      const sales = snapshot.docs.map(d => {
        const data = d.data();
        return {
          id: d.id,
          ...data,
          orderDate: data.orderDate?.toDate ? data.orderDate.toDate() : new Date(data.orderDate),
        };
      });

      setRawSales(sales);
    } catch (e) {
      console.error('Error loading sales:', e);
    } finally {
      setLoading(false);
    }
  };

  const getDateRange = () => {
    // Day boundaries follow the WORKING LOCATION's timezone.
    const now = new Date();
    const [, endTodayUtc] = tzDayRangeUtc(tzToday(reportTzValue), reportTzValue);
    const end = endTodayUtc;
    let start = new Date(now);

    switch (dateRange) {
      case 'today':
        start = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        break;
      case 'yesterday':
        start = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1);
        end.setDate(end.getDate() - 1);
        end.setHours(23, 59, 59);
        break;
      case 'last7days':
        start.setDate(start.getDate() - 7);
        break;
      case 'last30days':
        start.setDate(start.getDate() - 30);
        break;
      case 'thisMonth':
        start = new Date(now.getFullYear(), now.getMonth(), 1);
        break;
      case 'lastMonth':
        start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        end.setDate(0);
        end.setHours(23, 59, 59);
        break;
      case 'custom':
        if (customStart && customEnd) {
          start = new Date(customStart);
          const e = new Date(customEnd);
          e.setHours(23, 59, 59);
          return { start, end: e };
        }
        break;
      default:
        start.setDate(start.getDate() - 30);
    }
    return { start, end };
  };

  const filteredSales = useMemo(() => {
    return rawSales.filter(s => {
      if (selectedLocations.length > 0 && !selectedLocations.includes(s.locationId)) return false;
      if (statusFilter !== 'all' && (s.orderStatus || s.status || 'completed') !== statusFilter) return false;
      if (paymentFilter !== 'all' && !paymentMatchesFilter(s, paymentFilter)) return false;
      if (orderTypeFilter !== 'all' && (s.orderType || '') !== orderTypeFilter) return false;
      return true;
    });
  }, [rawSales, selectedLocations, statusFilter, paymentFilter, orderTypeFilter]);

  const metrics = useMemo(() => {
    const totalRevenue = filteredSales.reduce((sum, s) => sum + (s.total || 0), 0);
    const totalOrders = filteredSales.length;
    const totalItems = filteredSales.reduce((sum, s) => sum + (s.itemsQty || 0), 0);
    const avgOrderValue = totalOrders > 0 ? totalRevenue / totalOrders : 0;
    const avgItemsPerOrder = totalOrders > 0 ? totalItems / totalOrders : 0;
    return { totalRevenue, totalOrders, totalItems, avgOrderValue, avgItemsPerOrder };
  }, [filteredSales]);

  const dailyChartData = useMemo(() => {
    const map = {};
    filteredSales.forEach(s => {
      // Bucket by the LOCATION's local day (was toISOString = UTC day).
      const key = tzDateKey(s.orderDate, reportTzValue);
      if (!map[key]) map[key] = { date: key, revenue: 0, orders: 0, items: 0 };
      map[key].revenue += s.total || 0;
      map[key].orders += 1;
      map[key].items += s.itemsQty || 0;
    });
    return Object.values(map).sort((a, b) => a.date.localeCompare(b.date));
  }, [filteredSales]);

  const locationChartData = useMemo(() => {
    const map = {};
    filteredSales.forEach(s => {
      const name = s.locationName || locations.find(l => l.id === s.locationId)?.companyName || 'Unknown';
      if (!map[name]) map[name] = { name, revenue: 0, orders: 0 };
      map[name].revenue += s.total || 0;
      map[name].orders += 1;
    });
    return Object.values(map).sort((a, b) => b.revenue - a.revenue);
  }, [filteredSales, locations]);

  const paymentChartData = useMemo(() => {
    const map = {};
    filteredSales.forEach(s => {
      // Group the card family together in the chart (terminal + tap-to-pay +
      // stripe are all card money) — raw paymentType split hid real volume.
      const type = (s.paymentType || 'cash').toLowerCase();
      const family = CARD_PAYMENT_TYPES.includes(type) ? 'card' : type;
      const label = family.charAt(0).toUpperCase() + family.slice(1);
      if (!map[label]) map[label] = { name: label, value: 0 };
      map[label].value += s.total || 0;
    });
    return Object.values(map);
  }, [filteredSales]);

  const orderTypeChartData = useMemo(() => {
    const map = {};
    filteredSales.forEach(s => {
      const type = s.orderType || 'Unknown';
      if (!map[type]) map[type] = { name: type, orders: 0 };
      map[type].orders += 1;
    });
    return Object.values(map);
  }, [filteredSales]);

  const hourlyChartData = useMemo(() => {
    const hours = Array.from({ length: 24 }, (_, i) => ({ hour: `${i}:00`, orders: 0, revenue: 0 }));
    filteredSales.forEach(s => {
      // The location's local hour (was the viewer's device hour).
      const h = tzHour(s.orderDate, reportTzValue);
      hours[h].orders += 1;
      hours[h].revenue += s.total || 0;
    });
    return hours;
  }, [filteredSales]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);
  };

  const downloadCSV = () => {
    const headers = ['Date', 'Order #', 'Location', 'Customer', 'Order Type', 'Payment', 'Status', 'Items', 'Subtotal', 'Tax', 'Tip', 'Discount', 'Total'];
    const rows = filteredSales.map(s => [
      s.orderDate.toLocaleString(),
      s.confirmationNumber || s.id.substring(0, 8),
      s.locationName || '',
      s.customerName || 'Walk-in',
      s.orderType || '',
      s.paymentType || 'cash',
      s.status || 'completed',
      s.itemsQty || 0,
      s.subtotal || 0,
      s.tax || 0,
      s.tip || 0,
      s.discount || 0,
      s.total || 0,
    ]);
    const csv = [headers.join(','), ...rows.map(r => r.map(v => `"${v}"`).join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `sales-report-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const downloadJSON = () => {
    const data = filteredSales.map(s => ({
      id: s.id,
      date: s.orderDate.toISOString(),
      orderNumber: s.confirmationNumber || s.id.substring(0, 8),
      location: s.locationName || '',
      customer: s.customerName || 'Walk-in',
      orderType: s.orderType || '',
      paymentType: s.paymentType || 'cash',
      status: s.status || 'completed',
      itemsQty: s.itemsQty || 0,
      subtotal: s.subtotal || 0,
      tax: s.tax || 0,
      tip: s.tip || 0,
      discount: s.discount || 0,
      total: s.total || 0,
    }));
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `sales-report-${new Date().toISOString().split('T')[0]}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const toggleLocation = (id) => {
    setSelectedLocations(prev =>
      prev.includes(id) ? prev.filter(l => l !== id) : [...prev, id]
    );
  };

  if (loading && rawSales.length === 0) {
    return (
      <AdminLayout title="Sales Reports">
        <div className="text-center py-5">
          <img src="/fodyfee_shining.gif" alt="Loading..." style={{ width: 80, height: 80, objectFit: 'contain' }} />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Sales Reports">
      <div className="sales-reports">
        {/* Locations selector — TOP of the page: drives the Day Close
            Report (multi-location comparison + combined TOTAL) AND the
            charts/tables below. Empty selection = all locations for charts,
            working location for the day close. */}
        <div className="card mb-4">
          <div className="card-body py-3">
            <div className="d-flex align-items-center flex-wrap" style={{ gap: 8 }}>
              <span className="font-weight-bold small text-muted mr-1">
                <i className="fas fa-store mr-1"></i>Locations:
              </span>
              {locations.map(loc => (
                <button
                  key={loc.id}
                  className={`btn btn-sm ${selectedLocations.includes(loc.id) ? 'btn-primary' : 'btn-outline-secondary'}`}
                  style={selectedLocations.includes(loc.id) ? { backgroundColor: '#ff6b35', borderColor: '#ff6b35' } : {}}
                  onClick={() => toggleLocation(loc.id)}
                >
                  {loc.companyName || loc.locationName}
                </button>
              ))}
              {locations.length === 0 && (
                <span className="text-muted small">No locations found</span>
              )}
            </div>
            <div className="text-muted mt-2" style={{ fontSize: 12 }}>
              {selectedLocations.length === 0
                ? 'All locations are included in the charts. Select one or more to compare them in the Day Close Report.'
                : `Day Close Report compares ${selectedLocations.length} selected location${selectedLocations.length === 1 ? '' : 's'} with a combined TOTAL.`}
            </div>
          </div>
        </div>

        {/* Day Close Report (Z-report) — compares the locations selected
            above (or the top-bar working location when nothing is selected). */}
        {/* Tabs: Charts (default) · Day Close Report · Tax Report */}
        <ul className="nav nav-tabs mb-4" style={{ borderBottom: '2px solid #e5e7eb' }}>
          {[
            ['charts', 'Charts', 'fa-chart-line'],
            ['dayclose', 'Day Close Report', 'fa-moon'],
            ['tax', 'Tax Report', 'fa-landmark'],
            ['holdings', 'Tax Holdings', 'fa-book'],
            ['yearend', 'Year-End', 'fa-file-invoice-dollar'],
          ].map(([id, label, icon]) => (
            <li className="nav-item" key={id}>
              <button
                className={`nav-link ${activeTab === id ? 'active' : ''}`}
                style={activeTab === id
                  ? { color: '#ff6b35', fontWeight: 700, borderBottom: '3px solid #ff6b35', borderTop: 'none', borderLeft: 'none', borderRight: 'none', background: 'none' }
                  : { color: '#6b7280', border: 'none', background: 'none' }}
                onClick={() => setActiveTab(id)}
              >
                <i className={`fas ${icon} mr-1`}></i>{label}
              </button>
            </li>
          ))}
        </ul>

        {activeTab === 'dayclose' && (
          <DayCloseReport locations={locations} selectedLocationIds={selectedLocations} />
        )}
        {activeTab === 'tax' && (
          <TaxReport locations={locations} selectedLocationIds={selectedLocations} />
        )}
        {activeTab === 'holdings' && (
          <TaxHoldingsTab />
        )}
        {activeTab === 'yearend' && (
          <YearEndTab selectedLocationIds={selectedLocations} />
        )}

        {activeTab === 'charts' && (<>
        {/* Filters Bar */}
        <div className="card mb-4">
          <div className="card-body">
            <div className="row">
              <div className="col-md-3 mb-3">
                <label className="font-weight-bold small text-muted">Date Range</label>
                <select className="form-control" value={dateRange} onChange={e => setDateRange(e.target.value)}>
                  <option value="today">Today</option>
                  <option value="yesterday">Yesterday</option>
                  <option value="last7days">Last 7 Days</option>
                  <option value="last30days">Last 30 Days</option>
                  <option value="thisMonth">This Month</option>
                  <option value="lastMonth">Last Month</option>
                  <option value="custom">Custom</option>
                </select>
              </div>
              {dateRange === 'custom' && (
                <>
                  <div className="col-md-2 mb-3">
                    <label className="font-weight-bold small text-muted">From</label>
                    <input type="date" className="form-control" value={customStart} onChange={e => setCustomStart(e.target.value)} />
                  </div>
                  <div className="col-md-2 mb-3">
                    <label className="font-weight-bold small text-muted">To</label>
                    <input type="date" className="form-control" value={customEnd} onChange={e => setCustomEnd(e.target.value)} />
                  </div>
                </>
              )}
              <div className="col-md-2 mb-3">
                <label className="font-weight-bold small text-muted">Status</label>
                <select className="form-control" value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
                  <option value="all">All</option>
                  <option value="completed">Completed</option>
                  <option value="pending">Pending</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>
              <div className="col-md-2 mb-3">
                <label className="font-weight-bold small text-muted">Payment</label>
                <select className="form-control" value={paymentFilter} onChange={e => setPaymentFilter(e.target.value)}>
                  <option value="all">All</option>
                  <option value="cash">Cash</option>
                  <option value="card">Card</option>
                  <option value="stripe">Stripe</option>
                </select>
              </div>
              <div className="col-md-2 mb-3">
                <label className="font-weight-bold small text-muted">Order Type</label>
                <select className="form-control" value={orderTypeFilter} onChange={e => setOrderTypeFilter(e.target.value)}>
                  <option value="all">All</option>
                  <option value="delivery">Delivery</option>
                  <option value="pickup">Pickup</option>
                  <option value="dine-in">Dine-in</option>
                </select>
              </div>
            </div>

            {/* Export Actions */}
            <div className="mt-3 pt-3 d-flex justify-content-end" style={{ borderTop: '1px solid #f0f0f0' }}>
              <span className="text-muted mr-3 align-self-center">
                <strong>{filteredSales.length}</strong> orders found
              </span>
              <button className="btn btn-sm btn-outline-secondary mr-2" onClick={downloadCSV}>
                <i className="fas fa-file-csv mr-1"></i> Export CSV
              </button>
              <button className="btn btn-sm btn-outline-secondary" onClick={downloadJSON}>
                <i className="fas fa-file-code mr-1"></i> Export JSON
              </button>
            </div>
          </div>
        </div>

        {/* KPI Cards */}
        <div className="row mb-4">
          {[
            { label: 'Total Revenue', value: formatCurrency(metrics.totalRevenue), icon: 'fa-dollar-sign', color: '#ff6b35' },
            { label: 'Total Orders', value: metrics.totalOrders.toLocaleString(), icon: 'fa-shopping-cart', color: '#28a745' },
            { label: 'Avg Order Value', value: formatCurrency(metrics.avgOrderValue), icon: 'fa-receipt', color: '#17a2b8' },
            { label: 'Items Sold', value: metrics.totalItems.toLocaleString(), icon: 'fa-box-open', color: '#ffc107' },
            { label: 'Avg Items/Order', value: metrics.avgItemsPerOrder.toFixed(1), icon: 'fa-chart-bar', color: '#6f42c1' },
          ].map((kpi, idx) => (
            <div key={idx} className="col-lg col-md-4 col-6 mb-3">
              <div className="card h-100" style={{ borderRadius: '16px', border: 'none', boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }}>
                <div className="card-body d-flex align-items-center">
                  <div className="mr-3" style={{ width: 48, height: 48, borderRadius: 12, background: `${kpi.color}15`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <i className={`fas ${kpi.icon}`} style={{ color: kpi.color, fontSize: 20 }}></i>
                  </div>
                  <div>
                    <div className="text-muted small">{kpi.label}</div>
                    <div className="font-weight-bold" style={{ fontSize: '1.25rem', color: '#333' }}>{kpi.value}</div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Charts Row 1 */}
        <div className="row mb-4">
          <div className="col-lg-8 mb-4">
            <div className="card h-100" style={{ borderRadius: '16px', border: 'none', boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }}>
              <div className="card-header bg-white" style={{ borderBottom: '1px solid #f0f0f0', borderRadius: '16px 16px 0 0' }}>
                <h5 className="m-0 font-weight-bold">Revenue Trend</h5>
              </div>
              <div className="card-body">
                {dailyChartData.length === 0 ? (
                  <div className="text-center py-5 text-muted">No data available</div>
                ) : (
                  <ResponsiveContainer width="100%" height={300}>
                    <AreaChart data={dailyChartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                      <defs>
                        <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#ff6b35" stopOpacity={0.15} />
                          <stop offset="95%" stopColor="#ff6b35" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid vertical={false} stroke="#f0f0f0" />
                      <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#999' }} tickFormatter={v => {
                        const parts = v.split('-');
                        return `${parts[1]}/${parts[2]}`;
                      }} />
                      <YAxis tick={{ fontSize: 11, fill: '#999' }} tickFormatter={v => `$${v}`} />
                      <Tooltip formatter={v => formatCurrency(v)} contentStyle={{ borderRadius: 8, border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }} />
                      <Area type="monotone" dataKey="revenue" stroke="#ff6b35" strokeWidth={3} fill="url(#colorRevenue)" dot={{ r: 4, fill: '#fff', stroke: '#ff6b35', strokeWidth: 2 }} />
                    </AreaChart>
                  </ResponsiveContainer>
                )}
              </div>
            </div>
          </div>
          <div className="col-lg-4 mb-4">
            <div className="card h-100" style={{ borderRadius: '16px', border: 'none', boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }}>
              <div className="card-header bg-white" style={{ borderBottom: '1px solid #f0f0f0', borderRadius: '16px 16px 0 0' }}>
                <h5 className="m-0 font-weight-bold">Payment Methods</h5>
              </div>
              <div className="card-body">
                {paymentChartData.length === 0 ? (
                  <div className="text-center py-5 text-muted">No data available</div>
                ) : (
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie data={paymentChartData} cx="50%" cy="50%" innerRadius={60} outerRadius={90} paddingAngle={4} dataKey="value">
                        {paymentChartData.map((_, idx) => (
                          <Cell key={idx} fill={COLORS[idx % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip formatter={v => formatCurrency(v)} contentStyle={{ borderRadius: 8, border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }} />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Charts Row 2 */}
        <div className="row mb-4">
          <div className="col-lg-6 mb-4">
            <div className="card h-100" style={{ borderRadius: '16px', border: 'none', boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }}>
              <div className="card-header bg-white" style={{ borderBottom: '1px solid #f0f0f0', borderRadius: '16px 16px 0 0' }}>
                <h5 className="m-0 font-weight-bold">Sales by Location</h5>
              </div>
              <div className="card-body">
                {locationChartData.length === 0 ? (
                  <div className="text-center py-5 text-muted">No data available</div>
                ) : (
                  <ResponsiveContainer width="100%" height={280}>
                    <BarChart data={locationChartData} layout="vertical" margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#f0f0f0" />
                      <XAxis type="number" tick={{ fontSize: 11, fill: '#999' }} tickFormatter={v => `$${v}`} />
                      <YAxis type="category" dataKey="name" tick={{ fontSize: 11, fill: '#666' }} width={120} />
                      <Tooltip formatter={v => formatCurrency(v)} contentStyle={{ borderRadius: 8, border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }} />
                      <Bar dataKey="revenue" fill="#ff6b35" radius={[0, 8, 8, 0]} barSize={24} />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </div>
            </div>
          </div>
          <div className="col-lg-6 mb-4">
            <div className="card h-100" style={{ borderRadius: '16px', border: 'none', boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }}>
              <div className="card-header bg-white" style={{ borderBottom: '1px solid #f0f0f0', borderRadius: '16px 16px 0 0' }}>
                <h5 className="m-0 font-weight-bold">Orders by Type</h5>
              </div>
              <div className="card-body">
                {orderTypeChartData.length === 0 ? (
                  <div className="text-center py-5 text-muted">No data available</div>
                ) : (
                  <ResponsiveContainer width="100%" height={280}>
                    <BarChart data={orderTypeChartData} margin={{ top: 20, right: 30, left: 0, bottom: 0 }}>
                      <CartesianGrid vertical={false} stroke="#f0f0f0" />
                      <XAxis dataKey="name" tick={{ fontSize: 12, fill: '#666' }} />
                      <YAxis tick={{ fontSize: 11, fill: '#999' }} />
                      <Tooltip contentStyle={{ borderRadius: 8, border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }} />
                      <Bar dataKey="orders" fill="#ff8f35" radius={[8, 8, 0, 0]} barSize={48} />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Hourly Heatmap-style Chart */}
        <div className="row mb-4">
          <div className="col-12">
            <div className="card" style={{ borderRadius: '16px', border: 'none', boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }}>
              <div className="card-header bg-white" style={{ borderBottom: '1px solid #f0f0f0', borderRadius: '16px 16px 0 0' }}>
                <h5 className="m-0 font-weight-bold">Orders by Hour</h5>
              </div>
              <div className="card-body">
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart data={hourlyChartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                    <CartesianGrid vertical={false} stroke="#f0f0f0" />
                    <XAxis dataKey="hour" tick={{ fontSize: 11, fill: '#999' }} />
                    <YAxis tick={{ fontSize: 11, fill: '#999' }} />
                    <Tooltip contentStyle={{ borderRadius: 8, border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }} />
                    <Bar dataKey="orders" fill="#ffb347" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        </div>

        {/* Data Table */}
        <div className="card" style={{ borderRadius: '16px', border: 'none', boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }}>
          <div className="card-header bg-white d-flex justify-content-between align-items-center" style={{ borderBottom: '1px solid #f0f0f0', borderRadius: '16px 16px 0 0' }}>
            <h5 className="m-0 font-weight-bold">Transaction Details</h5>
            <span className="text-muted small">{filteredSales.length} records</span>
          </div>
          <div className="card-body p-0">
            <div className="table-responsive">
              <table className="table table-hover mb-0">
                <thead className="bg-light">
                  <tr>
                    <th style={{ borderTop: 'none' }}>Date</th>
                    <th style={{ borderTop: 'none' }}>Order #</th>
                    <th style={{ borderTop: 'none' }}>Location</th>
                    <th style={{ borderTop: 'none' }}>Customer</th>
                    <th style={{ borderTop: 'none' }}>Type</th>
                    <th style={{ borderTop: 'none' }}>Payment</th>
                    <th style={{ borderTop: 'none' }}>Status</th>
                    <th style={{ borderTop: 'none' }} className="text-right">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredSales.slice(0, 50).map(sale => (
                    <tr key={sale.id}>
                      <td className="text-nowrap">{sale.orderDate.toLocaleDateString()}</td>
                      <td><span className="font-weight-bold text-primary">{sale.confirmationNumber || sale.id.substring(0, 8)}</span></td>
                      <td>{sale.locationName || '-'}</td>
                      <td>{sale.customerName || 'Walk-in'}</td>
                      <td><span className="badge badge-light">{sale.orderType || '-'}</span></td>
                      <td><span className="badge badge-light text-uppercase">{sale.paymentType || 'cash'}</span></td>
                      <td>
                        <span className={`badge badge-${(sale.status || 'completed') === 'completed' ? 'success' : (sale.status === 'pending' ? 'warning' : 'secondary')}`}>
                          {sale.status || 'completed'}
                        </span>
                      </td>
                      <td className="text-right font-weight-bold" style={{ color: '#ff6b35' }}>{formatCurrency(sale.total)}</td>
                    </tr>
                  ))}
                  {filteredSales.length === 0 && (
                    <tr>
                      <td colSpan={8} className="text-center py-5 text-muted">
                        <i className="fas fa-inbox fa-2x mb-3"></i>
                        <p>No transactions found for the selected filters</p>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            {filteredSales.length > 50 && (
              <div className="text-center py-3 text-muted small">
                Showing first 50 of {filteredSales.length} records. Export to see all.
              </div>
            )}
          </div>
        </div>
        </>)}
      </div>
    </AdminLayout>
  );
};

export default SalesReports;
